import DueDate from '@components/Comment/DueDate.js';
import toast from '@components/Common/Toast.js';
import { CalendarPlusIcon, XIcon } from '@icons/IconLibrary';
import { UIDiv, UIText } from '@ui';
import { updateColumnValue } from '@utils/columnUtils';
import { formatDateForInput, formatDueDate } from '@utils/dateUtils.js';

export default class DateCell extends UIDiv {
  constructor({
    column,
    item = {},
    value = '',
    canEdit = true,
    onValueChange = null,
  }) {
    super();

    this.column = column;
    this.item = item;
    this.value = value;
    this.isEditMode = false;
    this.canEdit = canEdit;
    this.onValueChange = onValueChange; // Callback for value changes instead of API calls

    this.render();
  }

  render() {
    this.clear();

    if (
      !this.canEdit ||
      (!this.column.isCustom && this.column.key === 'createdAt')
    ) {
      this.renderUneditableInput();
      return;
    }

    if (this.value === null || this.value.trim() === '') {
      this.renderEmptyState();
    } else {
      this.renderTextState();
    }
  }

  renderEmptyState() {
    const emptyContainer = new UIDiv();
    emptyContainer.addClass(
      'group hover:border hover:border-gray-300 hover:bg-gray-50 rounded-md min-h-[34px] cursor-text flex items-center justify-center px-2',
    );

    // Always show user icon
    const calendarIcon = new CalendarPlusIcon({
      size: 18,
      color: 'none',
      strokeColor: '#525358',
    });
    emptyContainer.add(calendarIcon);

    // Click handler to enter edit mode
    emptyContainer.onClick(() => {
      this.enterEditMode();
    });
    emptyContainer.setAttribute('tabindex', '0');
    emptyContainer.onKeyDown(event => {
      if (event.key === 'Enter' && !this.isEditMode) {
        event.preventDefault();
        this.enterEditMode();
      }
    });

    this.add(emptyContainer);
  }

  renderTextState() {
    const textContainer = new UIDiv();
    textContainer.addClass(
      'group hover:border hover:border-gray-300 hover:bg-gray-50 rounded-md min-h-[34px] cursor-text truncate relative flex items-center gap-0.5',
    );

    const textContent = new UIText(formatDueDate(this.value));
    textContent.addClass('truncate flex-1');
    textContainer.add(textContent);

    // Click handler to enter edit mode
    textContainer.onClick(() => {
      this.enterEditMode();
    });
    textContainer.setAttribute('tabindex', '0');
    textContainer.onKeyDown(event => {
      if (event.key === 'Enter' && !this.isEditMode) {
        event.preventDefault();
        this.enterEditMode();
      }
    });

    // hover show clear icon on right
    const clearIcon = new XIcon({
      size: 12,
      className:
        'absolute right-2 top-1/2 transform -translate-y-1/2 hidden group-hover:!flex justify-center items-center bg-gray-200 hover:bg-gray-300 rounded-md cursor-pointer !w-5 !h-5',
    });
    clearIcon.onClick(async event => {
      event.stopPropagation(); // Prevent triggering the click handler
      try {
        // If onValueChange callback is provided, use it instead of API call
        if (this.onValueChange) {
          this.onValueChange('');
          // Update the local value
          this.value = '';
          this.exitEditMode();
        } else {
          // Update the value via API if we have the necessary information
          const entityType = !this.item.reconstructions ? 'folder' : 'file';
          const entityId = this.item.id || this.item.sharingId;

          await updateColumnValue(this.column.id, entityType, entityId, '');

          // Update the local value
          this.value = '';
          if (this.item) {
            this.item[this.column.key] = '';
          }
          this.exitEditMode();
        }
      } catch (error) {
        console.error('Failed to update column value:', error);
        toast.error(i18next.t('pages.projects.toast.failed_column_update'));
      }
    });
    textContainer.add(clearIcon);

    this.add(textContainer);
  }

  enterEditMode() {
    if (this.isEditMode) return;

    this.isEditMode = true;
    this.clear();

    // Create input that fills the cell
    const input = new DueDate({
      id: `date-${this.column.id}-${this.item?.id || 'new'}`,
      initialValue: formatDateForInput(this.value),
      showTitle: false,
      placeholder: this.value
        ? formatDueDate(this.value)
        : i18next.t('pages.projects.cells.date.selectDate'),
      showIcon: false,
      defaultDate: null,
      onUpdate: async newValue => {
        if (newValue === this.value) {
          this.exitEditMode();
          return;
        }

        try {
          // If onValueChange callback is provided, use it instead of API call
          if (this.onValueChange) {
            this.onValueChange(newValue);
            // Update the local value
            this.value = newValue;
            this.exitEditMode();
          } else {
            // Update the value via API if we have the necessary information
            const entityType = !this.item.reconstructions ? 'folder' : 'file';
            const entityId = this.item.id || this.item.sharingId;

            await updateColumnValue(
              this.column.id,
              entityType,
              entityId,
              newValue,
            );

            // Update the local value
            this.value = newValue;
            if (this.item) {
              this.item[this.column.key] = newValue;
            }

            this.exitEditMode();
          }
        } catch (error) {
          toast.error(i18next.t('pages.projects.toast.failed_column_update'));
          // Revert to original value on error
          this.exitEditMode();
        }
      },
      onClose: () => {
        this.exitEditMode();
      },
      readonly: !this.canEdit,
    });

    this.add(input);

    input.input.triggerFocus();
  }

  exitEditMode() {
    this.isEditMode = false;
    this.render();
  }

  renderUneditableInput() {
    const textContainer = new UIDiv();
    textContainer.addClass('min-h-[34px] flex items-center truncate');

    const textContent = new UIText(formatDueDate(this.value));
    textContent.addClass('truncate flex-1');
    textContainer.add(textContent);

    this.add(textContainer);
  }
}
